/**
 * Public site entry point.
 *
 * The pages are prerendered to real HTML at build time, so this hydrates rather than
 * renders: the content is on screen before this script runs, and JavaScript only adds
 * the create form's behaviour.
 */

import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';

import '../client/styles/tokens.css';
import '../client/styles/ui.css';
import '../client/styles/public.css';

import { resolvePublicLocale } from './lib/language.js';
import { loadContent } from './lib/load-content.js';
import { PublicApp } from './public/PublicApp.js';

const container = document.getElementById('root');
/*
 * The language comes from the URL, exactly as the prerenderer wrote it, so the markup
 * React hydrates matches the markup the server sent.
 *
 * The one exception is a first visit to an English URL from a browser that asks for
 * another language: `resolvePublicLocale` starts a redirect and returns null, and we skip
 * hydration entirely rather than render a page that is about to be replaced.
 */
const resolved = container ? resolvePublicLocale() : null;

if (container && resolved) {
  const { locale, path } = resolved;

  /*
   * The prose for this language is fetched before hydrating, never after. The page is
   * already painted and readable at this point; the only thing waiting is the create
   * form's behaviour, which is worth a few milliseconds to avoid shipping four
   * translations of every page to every visitor.
   */
  void loadContent(locale).then(() => {
    hydrateRoot(
      container,
      <StrictMode>
        <PublicApp path={path} locale={locale} />
      </StrictMode>,
      {
        // Hydration mismatches are silent in production but are real bugs: they throw
        // away the prerendered DOM and re-render. In a development build they are
        // collected so the build QA step can assert there are none.
        onRecoverableError(error) {
          if (import.meta.env.DEV) {
            const store = ((
              window as unknown as { __hydrationErrors?: string[] }
            ).__hydrationErrors ??= []);
            store.push(String(error));
          }
        },
      },
    );
  });
}
